import React, { useMemo, useState, useCallback } from 'react';
import { AllWorkflowsChartProps, DashboardAPIResponse } from 'src/pages/dashboard/Dashboard.interface';
import { boardI18nStrings, boardItemI18nStrings, xTickFormatter, yTickFormatter, MIN_WIDTH_PER_WORKFLOW } from 'src/components/dashboard/DashboardConstants';
import { collectStatuses, buildStatusBarChartData } from 'src/components/dashboard/DashboardUtils';
import { useFetchForSDS } from 'src/hooks/useFetchForSDS';
import { SNOW_DATA_SERVICE_ENDPOINT } from 'src/config';
import Header from '@amzn/awsui-components-console/header';
import Spinner from '@amzn/awsui-components-console/spinner';
import Alert from '@amzn/awsui-components-console/alert';
import BarChart from '@amzn/awsui-components-console/bar-chart';
import Box from '@amzn/awsui-components-console/box';
import Board, { BoardProps } from '@amzn/awsui-board-components-console/board';
import BoardItem from '@amzn/awsui-board-components-console/board-item';
import EmptyState from 'src/components/commonComponents/EmptyState/EmptyState';

interface BoardItemData { title: string; type: 'chart' }

export default function AllWorkflowsChart({ year, month, workflowNameMap }: AllWorkflowsChartProps) {
	const queryParams = useMemo(() => {
		const params: Record<string, string> = { year };
		if (month) params.month = month;
		return params;
	}, [year, month]);

	const { isLoading, data, error } = useFetchForSDS<DashboardAPIResponse>({
		path: '/dashboard', method: 'GET', queryParams, url: SNOW_DATA_SERVICE_ENDPOINT,
	});

	const getFullName = useCallback((id: string) => workflowNameMap?.[id] || id, [workflowNameMap]);

	const allStatuses = useMemo(() => data?.items ? collectStatuses(data.items) : [], [data]);

	const { chartSeries, xDomain } = useMemo(() => {
		if (!data?.items) return { chartSeries: [], xDomain: [] };
		return {
			chartSeries: buildStatusBarChartData(data.items, allStatuses, getFullName),
			xDomain: data.items.map((w) => getFullName(w.workflowType)),
		};
	}, [data, allStatuses, getFullName]);

	const [boardItems, setBoardItems] = useState<BoardProps.Item<BoardItemData>[]>([{
		id: 'all-workflows-chart',
		definition: { defaultRowSpan: 6, defaultColumnSpan: 4, minRowSpan: 3, minColumnSpan: 2 },
		columnOffset: { 4: 0 }, rowSpan: 6, columnSpan: 4,
		data: { title: 'All Workflows Status Overview', type: 'chart' },
	}]);

	const renderContent = () => {
		if (isLoading) return <Box textAlign="center" padding={{ vertical: 'xxl' }}><Spinner size="large" /></Box>;
		if (error) return <Alert type="error" header="Error loading data">{error}</Alert>;

		return (
				<div style={{ overflowX: 'auto', height: '100%', minWidth: `${(data?.items?.length || 0) * MIN_WIDTH_PER_WORKFLOW}px`}}>
					<BarChart
						series={chartSeries}
						xDomain={xDomain}
						yTitle="Count"
						xTitle="Workflow Type"
						stackedBars
						fitHeight
						hideFilter
						emphasizeBaselineAxis
						i18nStrings={{
							xTickFormatter,
							yTickFormatter,
						}}
						ariaLabel="Workflow status distribution"
						empty={<EmptyState title="No workflow data available for the selected period." />}
					/>
				</div>
		);
	};

	return (
		<Board<BoardItemData>
			items={boardItems}
			onItemsChange={({ detail }) => setBoardItems(detail.items as BoardProps.Item<BoardItemData>[])}
			i18nStrings={boardI18nStrings}
			empty={<EmptyState title="No widgets available" />}
			renderItem={(item) => (
				<BoardItem
					header={
						<Header description={data && !isLoading ? `Showing ${data.items?.length || 0} workflow types for ${month ? `${year}-${month}` : year}` : undefined}>
							{item.data?.title}
						</Header>
					}
					i18nStrings={boardItemI18nStrings}
				>
					{renderContent()}
				</BoardItem>
			)}
		/>
	);
}
